import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';
import { AddAPhoto as AddAPhotoIcon } from '@mui/icons-material';
import {
  Button,
  Input,
  InputContainer,
  InputPrice,
  InputNumber,
  SearchableSelect,
  Select,
  ImageGallery,
  ImageGalleryCard,
  ImageGalleryCardWrapper,
  Typography,
  enqueueSnackbar,
  fullScreenButtonClick,
} from '../../design-system';
import { DashboardWrapper, DashboardInputs, DashbaordFormFooter, HideInput } from '../dashboard';
import {
  useConvertCurrencyFcQuery,
  useSetPriceRepairMutation,
  useUpdateImagesRepairMutation,
} from '../../hooks';
import { containersService } from '../../services';
import { extendContainerValidator, filesFormatValidator } from '../../utils';
import { CURRENCIES, currencyOptions, INPUT_ERRORS, MESSAGES, TABLE } from '../../const';
import { CurrencyCode } from '../../types';

export const RepairsAdd = () => {
  const navigate = useNavigate();
  const setPriceMutation = useSetPriceRepairMutation();
  const updateImagesMutation = useUpdateImagesRepairMutation();
  const convertCurrency = useConvertCurrencyFcQuery();

  const containerId = useRef('');
  const [containerNumber, setContainerNumber] = useState('');
  const [price, setPrice] = useState('0');
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('USD');
  const [expectedDurationDays, setExpectedDurationDays] = useState('0');
  const [description, setDescription] = useState('');
  const [beforeImages, setBeforeImages] = useState<any[]>([]);
  const [beforeImagesFiles, setBeforeImagesFiles] = useState<any[]>([]);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const beforeImagesInputRef = useRef<any>();

  const resetRepairForm = () => {
    setPrice('0');
    setCurrencyCode('USD');
    setExpectedDurationDays('0');
    setDescription('');
    setBeforeImages([]);
    setBeforeImagesFiles([]);
    setIsSubmitClicked(false);
    setFormKey((key) => key + 1);

    if (beforeImagesInputRef.current) {
      beforeImagesInputRef.current.type = 'text';
      beforeImagesInputRef.current.type = 'file';
    }
  };

  const handleContainerSelect = (option: any) => {
    containerId.current = option.id;
    setContainerNumber(option.label);
    resetRepairForm();
  };

  const containerOptionsQueryFn = (number: string) =>
    containersService.options({
      hasLocation: 'all',
      hideAwaitingPayment: true,
      allowHold: true,
      take: TABLE.PAGE_MAX_SIZE,
      skip: 0,
      isQuery: true,
      notLoading: true,
      ...(number?.length >= 3 && extendContainerValidator(number) ? { number } : {}),
    });

  const handleCurrencyChange = (newCurrencyCode: CurrencyCode) => {
    if (newCurrencyCode === currencyCode) return;

    const convertedValue = convertCurrency(currencyCode, newCurrencyCode, Number(price) || 0);

    setPrice(convertedValue.toFixed(2));
    setCurrencyCode(newCurrencyCode);
    setFormKey((key) => key + 1);
  };

  const validators = {
    containerNumber: !containerId.current ? INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER : '',
    price: !String(price).length
      ? INPUT_ERRORS.REQUIRED.PRICE
      : Number(price) <= 0
      ? INPUT_ERRORS.NOT_VALID.PRICE_MIN_VALUE
      : '',
    expectedDurationDays: !String(expectedDurationDays).length
      ? INPUT_ERRORS.REQUIRED.DAYS_TO_COMPLETE
      : Number(expectedDurationDays) <= 0
      ? INPUT_ERRORS.NOT_VALID.DAYS_TO_COMPLETE_MIN_VALUE
      : '',
    description: !description
      ? INPUT_ERRORS.REQUIRED.DESCRIPTION
      : !validator.isLength(description, { min: 10, max: 500 })
      ? INPUT_ERRORS.NOT_VALID.REPAIR_DESCRIPTION_LENGTH
      : '',
    beforeImages: !beforeImages.length ? INPUT_ERRORS.REQUIRED.IMAGES : '',
  };

  const handleChangeBeforeImages = (newImages: any[], newFiles: any[]) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    setBeforeImages((prev) => [...prev, ...newImages]);
    setBeforeImagesFiles((prev) => [...prev, ...newFiles]);
    beforeImagesInputRef.current.type = 'text';
    beforeImagesInputRef.current.type = 'file';
  };

  const handleDeleteBeforeImageClick = (index: number) => {
    setBeforeImages(beforeImages.filter((_, i) => i !== index));
    setBeforeImagesFiles(beforeImagesFiles.filter((_, i) => i !== index));
  };

  const handleZoomImageClick = (i: number) => {
    setGalleryImages(beforeImages);
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  const handleSubmitClick = async () => {
    setIsSubmitClicked(true);

    if (beforeImages.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (Object.values(validators).some((error) => error)) {
      return;
    }

    try {
      const response = await setPriceMutation.mutateAsync([
        {
          id: containerId.current,
          price: Number(price),
          currencyCode,
          expectedDurationDays: Number(expectedDurationDays),
          description,
        },
      ]);

      const repairId = response.data.ids[0];

      await updateImagesMutation.mutateAsync({
        id: repairId,
        photos: beforeImagesFiles.map((file) => ({
          action: 'upload' as const,
          file,
        })),
        action: 'before',
      });

      enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.CREATED, 'success');
      navigate('/dashboard/repairs');
    } catch {
      enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_SET, 'error');
    }
  };

  return (
    <DashboardWrapper>
      <ImageGallery photos={galleryImages} startIndex={zoomImageIndex} />
      <Typography variant={'textXL'} weight={'bold'}>
        Repairs Add
      </Typography>
      <DashboardInputs>
        <SearchableSelect
          InputComponent={InputContainer}
          label={'Container*'}
          selectedOptionLabel={containerNumber || 'Select Container'}
          onSelect={handleContainerSelect}
          queryKey={['container-short', 'repairs-add', containerNumber]}
          inputValueKey={(inputValue: string) => (inputValue.length < 3 ? '' : inputValue)}
          queryFn={containerOptionsQueryFn}
          selectedAreasEqaul={{ key: 'id', keys: [containerId.current] }}
          error={isSubmitClicked && !!validators.containerNumber}
          helperText={isSubmitClicked ? validators.containerNumber : ''}
        />
        {!!containerId.current && (
          <>
            <DashboardInputs columns={'1fr max-content'}>
              <Typography
                variant={'textM'}
                weight={'bold'}
                color={isSubmitClicked && !!validators.beforeImages ? '--red-100' : '--gray-100'}
              >
                Before Images*
              </Typography>
              <Button onClick={() => beforeImagesInputRef.current.click()} isSquare>
                <AddAPhotoIcon />
              </Button>
            </DashboardInputs>
            <HideInput>
              <Input
                label={'Before Images'}
                data-size={'medium'}
                type={'file'}
                inputRef={beforeImagesInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                }}
                value={undefined}
                handleChangeFiles={handleChangeBeforeImages}
              />
            </HideInput>
            <ImageGalleryCardWrapper>
              {beforeImages.map((img: any, i: number) => (
                <ImageGalleryCard
                  key={i}
                  img={img}
                  handleZoomClick={() => handleZoomImageClick(i)}
                  handleDeleteImageClick={() => handleDeleteBeforeImageClick(i)}
                  hideDeleteButton={beforeImages.length <= 1}
                />
              ))}
            </ImageGalleryCardWrapper>

            <Select
              label={'Currency*'}
              placeholder={'Select Currency'}
              options={currencyOptions}
              value={currencyCode}
              onSelect={handleCurrencyChange}
            />
            <InputPrice
              key={`price-${formKey}`}
              label={'Price*'}
              data-size={'medium'}
              defaultValue={price}
              onChange={(e) => setPrice(e.target.value)}
              error={isSubmitClicked && !!validators.price}
              helperText={isSubmitClicked ? validators.price : ''}
              endIcon={CURRENCIES[currencyCode].symbol}
            />
            <InputNumber
              key={`days-${formKey}`}
              label={'Days To Complete*'}
              placeholder={'Days'}
              data-size={'medium'}
              defaultValue={expectedDurationDays}
              onChange={(e) => setExpectedDurationDays(e.target.value)}
              error={isSubmitClicked && !!validators.expectedDurationDays}
              helperText={isSubmitClicked ? validators.expectedDurationDays : ''}
            />
            <Input
              label={'Description*'}
              data-size={'medium'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              multiline
              error={isSubmitClicked && !!validators.description}
              helperText={isSubmitClicked ? validators.description : ''}
            />
          </>
        )}
      </DashboardInputs>

      {!!containerId.current && (
        <DashbaordFormFooter>
          <Button size={'medium'} onClick={handleSubmitClick}>
            Submit
          </Button>
        </DashbaordFormFooter>
      )}
    </DashboardWrapper>
  );
};
